import { useMemo, useState } from 'react';
import { endOfWeek, format, isBefore, startOfDay } from 'date-fns';
import { Link, useLocation } from 'react-router-dom';
import { ClipboardDocumentCheckIcon, PlusIcon } from '@heroicons/react/24/outline';
import { useSchool } from '@/contexts/SchoolContext';
import { useRoleSwitcher, useAuth } from '@/features/auth/hooks';
import useSubstratePanelStore from '@/stores/substratePanelStore';
import calendarEventService from '@/services/calendar/CalendarEventService';
import useCalendarOverview from '@/features/calendar/hooks/useCalendarOverview';
import CalendarAgendaList from '@/features/calendar/components/CalendarAgendaList';
import CalendarEventDetail from '@/features/calendar/components/CalendarEventDetail';
import CalendarEventModal from '@/features/calendar/components/CalendarEventModal';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import { toCalendarDate } from '@/features/calendar/utils/calendarDateUtils';

function WorkDue({ items, loading, error }) {
  if (loading) return <div className="skeleton h-16 w-full" />;
  if (items.length === 0) {
    return <p className="text-sm text-base-content/55">{error || 'Nothing due in the next 7 days.'}</p>;
  }

  const today = startOfDay(new Date());
  return (
    <div className="divide-y divide-base-200">
      {items.slice(0, 4).map((item) => {
        const due = toCalendarDate(item.dueDate);
        const overdue = due && isBefore(due, today);
        return (
          <Link
            key={`${item.kind}-${item.id}`}
            to={item.href}
            className="flex min-w-0 items-center gap-3 rounded py-2.5 text-left hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          >
            <span className={`w-20 shrink-0 text-xs font-semibold ${overdue ? 'text-error' : 'text-base-content/55'}`}>
              {overdue ? 'Overdue' : format(due, 'EEE d MMM')}
            </span>
            <span className="min-w-0 flex-1 truncate text-sm font-medium">{item.title}</span>
          </Link>
        );
      })}
    </div>
  );
}

/**
 * Calendar's contextual companion: quick school dates and work deadlines while
 * browsing, and focused event details when an event is opened from Calendar.
 */
export default function CalendarPanel() {
  const location = useLocation();
  const { schoolId } = useSchool();
  const { currentRole } = useRoleSwitcher();
  const { user } = useAuth();
  const panelContext = useSubstratePanelStore((state) => state.panelContext);
  const openPanel = useSubstratePanelStore((state) => state.openPanel);
  const canEdit = ['admin', 'dev'].includes(currentRole);
  const [view, setView] = useState('today');
  const [showModal, setShowModal] = useState(false);
  const {
    schoolName,
    today,
    events,
    todayEvents,
    workItems,
    eventsLoading,
    eventsError,
    workLoading,
    workError,
    refetch,
  } = useCalendarOverview();
  const weekEnd = useMemo(() => endOfWeek(today, { weekStartsOn: 1 }), [today]);
  const weekEvents = useMemo(() => events.filter((event) => {
    const start = toCalendarDate(event.startDate);
    const end = toCalendarDate(event.endDate);
    return start && end && start <= weekEnd && end >= today;
  }), [events, today, weekEnd]);
  const selectedEvent = panelContext?.kind === 'calendar-event' ? panelContext.event : null;
  const selectedEventDate = toCalendarDate(selectedEvent?.startDate);
  const shownEvents = view === 'today' ? todayEvents : weekEvents;

  const clearEvent = () => openPanel('calendar');

  const handleSave = async (eventData) => {
    if (eventData.id) {
      const { id, ...updates } = eventData;
      const updatedEvent = await calendarEventService.updateEvent(schoolId, id, updates);
      openPanel('calendar', {
        kind: 'calendar-event',
        event: { ...selectedEvent, ...updatedEvent },
      });
    } else {
      await calendarEventService.createEvent(schoolId, eventData, user.uid);
    }
    refetch();
  };

  const handleDelete = async (eventId) => {
    await calendarEventService.deleteEvent(schoolId, eventId);
    clearEvent();
    refetch();
  };

  return (
    <div className="p-4 pb-24">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-base-content/50">
            {schoolName}
          </p>
          <h2 className="mt-1 font-semibold">
            {format(selectedEventDate || today, 'EEEE, d MMMM')}
          </h2>
        </div>
        {canEdit && location.pathname !== '/calendar' && (
          <button
            type="button"
            className="btn btn-primary btn-xs gap-1"
            onClick={() => { clearEvent(); setShowModal(true); }}
          >
            <PlusIcon className="h-3.5 w-3.5" aria-hidden="true" />
            Add event
          </button>
        )}
      </div>

      {selectedEvent ? (
        <div className="mt-4">
          <CalendarEventDetail
            event={selectedEvent}
            canEdit={canEdit}
            canLoadAudienceOptions={currentRole !== 'parent'}
            onEdit={() => setShowModal(true)}
            onAudienceChanged={refetch}
          />
          <button type="button" className="btn btn-ghost btn-sm mt-2 w-full" onClick={clearEvent}>
            Back to calendar overview
          </button>
        </div>
      ) : (
        <>
          <div role="tablist" aria-label="Calendar reference range" className="mt-4 inline-flex w-full rounded-lg border border-base-300 bg-base-100 p-1">
            <button
              type="button"
              role="tab"
              aria-selected={view === 'today'}
              className={`flex-1 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                view === 'today' ? 'bg-base-200 text-base-content' : 'text-base-content/60 hover:text-base-content'
              }`}
              onClick={() => setView('today')}
            >
              Today
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={view === 'week'}
              className={`flex-1 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                view === 'week' ? 'bg-base-200 text-base-content' : 'text-base-content/60 hover:text-base-content'
              }`}
              onClick={() => setView('week')}
            >
              This week
            </button>
          </div>

          {eventsLoading ? (
            <div className="flex justify-center py-12"><LoadingSpinner /></div>
          ) : eventsError ? (
            <p className="mt-4 rounded-lg bg-error/10 p-3 text-sm text-error">Calendar dates are unavailable.</p>
          ) : (
            <div className="mt-4">
              <CalendarAgendaList
                events={shownEvents}
                emptyMessage={view === 'today' ? 'Nothing scheduled today.' : 'Nothing else scheduled this week.'}
                onEventClick={(event) => openPanel('calendar', { kind: 'calendar-event', event })}
                showDate={view === 'week'}
              />
            </div>
          )}

          <section className="mt-5 border-t border-base-200 pt-4" aria-labelledby="calendar-panel-work-title">
            <div className="mb-2 flex items-center gap-2">
              <ClipboardDocumentCheckIcon className="h-4 w-4 text-accent" aria-hidden="true" />
              <h3 id="calendar-panel-work-title" className="text-sm font-semibold">Work due</h3>
            </div>
            <WorkDue items={workItems} loading={workLoading} error={workError} />
          </section>
        </>
      )}

      {showModal && (
        <CalendarEventModal
          event={selectedEvent}
          initialDate={today}
          onSave={handleSave}
          onDelete={canEdit && selectedEvent ? handleDelete : null}
          onClose={() => setShowModal(false)}
        />
      )}
    </div>
  );
}
